/** Sprint Day 0 helpers — Day 0 is the selected start date; Day 1 is the next calendar day. */

export const DEFAULT_SPRINT_START = '2026-08-13';
export const DEFAULT_TOTAL_DAYS = 45;

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/** Inclusive end date for a sprint of `totalDays` counting Day 0 .. Day (totalDays - 1). */
export function sprintEndFromStart(startIso: string, totalDays = DEFAULT_TOTAL_DAYS): string {
  const start = parseIsoDate(startIso);
  const end = new Date(start);
  end.setDate(end.getDate() + Math.max(1, totalDays) - 1);
  return toIsoDate(end);
}

/** Next Monday on or after today (if today is Monday, returns today). */
export function nextMondayIso(from = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const add = day === 1 ? 0 : (8 - day) % 7;
  d.setDate(d.getDate() + add);
  return toIsoDate(d);
}

/**
 * Day index relative to Day 0:
 * - before start → 0 (still on / before Day 0 countdown; callers may show "starts in")
 * - on start date → 0
 * - each following day → 1, 2, … capped at totalDays - 1
 *
 * Display often uses "DAY {n} / {totalDays}" with n in 0..(totalDays-1).
 */
export function sprintDayIndex(startIso: string, now = new Date(), totalDays = DEFAULT_TOTAL_DAYS): number {
  const begin = parseIsoDate(startIso);
  begin.setHours(0, 0, 0, 0);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.floor((today.getTime() - begin.getTime()) / 86400000);
  if (diff < 0) return 0;
  return Math.min(totalDays - 1, diff);
}

/** Days until Day 0 (0 if Day 0 is today or past). */
export function daysUntilStart(startIso: string, now = new Date()): number {
  const begin = parseIsoDate(startIso);
  begin.setHours(0, 0, 0, 0);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.floor((begin.getTime() - today.getTime()) / 86400000));
}

export function formatSprintLabel(iso: string): string {
  const d = parseIsoDate(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
